<template>
  <div class="col-12 col-lg-6 col-xl-4 col-xxl-3">
    <component
      :is="componentType"
      v-bind="componentAttrs"
      class="card h-100 border-translucent text-decoration-none report-card"
      @click="handleClick"
      @keydown.enter="handleClick"
      @keydown.space.prevent="handleClick"
    >
      <div class="card-body d-flex p-0">
        <div
          class="icon-section d-flex align-items-center justify-content-center"
        >
          <font-awesome-icon :icon="icon" />
        </div>

        <div class="flex-grow-1 d-flex flex-column p-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <h6 class="card-title mb-0 text-body-emphasis fs-8">{{ title }}</h6>
            <span
              v-if="badge"
              class="badge bg-primary-subtle text-primary-emphasis flex-shrink-0"
            >
              {{ badge }}
            </span>
          </div>

          <p class="card-text text-muted small mb-2">
            {{ description }}
          </p>

          <div class="mt-auto">
            <span class="text-primary small fw-semibold">
              <font-awesome-icon :icon="actionIcon" class="me-1" />
              {{ actionText }}
            </span>
          </div>
        </div>
      </div>
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = defineProps<{
  title: string;
  description: string;
  icon: string;
  actionIcon: string;
  actionText: string;
  href?: string;
  badge?: string;
}>();

const emit = defineEmits<{
  "open:report": [];
}>();

const isLink = computed(() => !!props.href);

const componentType = computed(() => (isLink.value ? "a" : "div"));

const componentAttrs = computed(() => {
  if (isLink.value) {
    return {
      href: props.href,
      target: "_blank",
      rel: "noopener noreferrer",
    };
  }
  return {
    role: "button",
    tabindex: 0,
  };
});

const handleClick = () => {
  if (!isLink.value) {
    emit("open:report");
  }
};
</script>

<style scoped lang="scss">
.report-card {
  transition: all 0.2s ease-in-out;
  color: inherit;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);

    .text-primary {
      text-decoration: underline;
    }
  }

  &:focus {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: 2px;
  }
}

.icon-section {
  width: 60px;
  flex-shrink: 0;
  background-color: rgba(var(--phoenix-primary-rgb), 0.1);
  color: var(--phoenix-primary);
  font-size: 1.5rem;
}
</style>
